import { CryptoBalanceEntry, CryptoToken, getNetworkInfo } from "./types";
import { TokenIcon, NetworkIcon } from "./CryptoIcons";
import { ArrowDownToLine, ArrowUpFromLine } from "lucide-react";

interface Props {
  token: CryptoToken;
  total: number;
  networkBalances: CryptoBalanceEntry[];
  onFund: () => void;
  onWithdraw: () => void;
}

const CryptoWalletCard = ({ token, total, networkBalances, onFund, onWithdraw }: Props) => {
  return (
    <div
      className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm
        dark:border-gray-800 dark:bg-white/[0.03]"
    >
      {/* Header row: token identity + total balance side by side */}
      <div className="mb-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <TokenIcon token={token} size={28} />
          <span className="text-sm font-medium text-gray-800 dark:text-white/90">{token}</span>
        </div>

        <div className="text-right">
          <div className="text-lg font-bold leading-tight text-gray-900 dark:text-white">
            {total.toLocaleString(undefined, { maximumFractionDigits: 8 })}
          </div>
          <div className="text-xs text-gray-400 dark:text-gray-500">Total balance</div>
        </div>
      </div>

      {/* Per-network breakdown */}
      <div className="mb-4 space-y-1.5 border-t border-gray-100 pt-3 dark:border-gray-800">
        {networkBalances.map((entry) => (
          <div
            key={entry.network}
            className="flex items-center justify-between rounded-lg bg-gray-50 px-3 py-2 dark:bg-white/[0.03]  hover:bg-gray-70 dark:hover:bg-white/[0.06]"
          >
            <div className="flex items-center gap-2">
              <NetworkIcon network={entry.network} size={16} />
              <span className="text-xs text-gray-600 dark:text-gray-400">
                {getNetworkInfo(entry.network).name}
              </span>
              {!entry.available && (
                <span className="rounded-full bg-gray-200 px-2 py-0.5 text-[10px] font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-400">
                  Unavailable
                </span>
              )}
            </div>
            <span className="text-xs font-medium text-gray-700 dark:text-gray-300">$
              {entry.balance.toLocaleString(undefined, { maximumFractionDigits: 8 })}
            </span>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-2">
        <button
          onClick={onFund}
          className="flex items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
        >
          <ArrowDownToLine className="h-4 w-4 text-green-600 dark:text-green-400" />
          Fund
        </button>
        <button
          onClick={onWithdraw}
          className="flex items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
        >
          <ArrowUpFromLine className="h-4 w-4 text-blue-600 dark:text-blue-400" />
          Withdraw
        </button>
      </div>
    </div>
  );
};

export default CryptoWalletCard;